import React from 'react';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';

// Variant types
export type TypographyVariant =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  | 'subtitle1' | 'subtitle2'
  | 'body1' | 'body2'
  | 'caption' | 'overline';

export type TypographyColor =
  | 'primary' | 'secondary' | 'default' | 'muted'
  | 'error' | 'success' | 'warning' | 'info' | 'inherit';

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify';
export type TypographyWeight = 300 | 400 | 500 | 600 | 700;
export interface TypographyProps {
  variant?: TypographyVariant;
  color?: TypographyColor | string;
  align?: TypographyAlign;
  weight?: TypographyWeight;
  children?: React.ReactNode;
  className?: string;
  as?: keyof React.JSX.IntrinsicElements;
  noMargin?: boolean;
}

// Tailwind classes for variants - size, weight, line-height (no margin)
const variantBaseClasses: Record<TypographyVariant, string> = {
  h1: 'text-[2rem] font-semibold leading-tight',
  h2: 'text-[1.75rem] font-semibold leading-tight',
  h3: 'text-[1.5rem] font-semibold leading-snug',
  h4: 'text-[1.25rem] font-semibold leading-snug',
  h5: 'text-[1.125rem] font-medium leading-normal',
  h6: 'text-base font-medium leading-normal',
  subtitle1: 'text-base font-normal leading-relaxed',
  subtitle2: 'text-sm font-medium leading-relaxed',
  body1: 'text-base font-normal leading-relaxed',
  body2: 'text-sm font-normal leading-relaxed',
  caption: 'text-xs font-normal leading-relaxed',
  overline: 'text-xs font-medium leading-relaxed uppercase tracking-wider',
};

// Default margin classes for each variant (applied when noMargin is false)
const variantMargins: Record<TypographyVariant, string> = {
  h1: 'mb-4',
  h2: 'mb-3.5',
  h3: 'mb-3',
  h4: 'mb-2.5',
  h5: 'mb-2',
  h6: 'mb-1.5',
  subtitle1: 'mb-2',
  subtitle2: 'mb-1.5',
  body1: 'mb-2',
  body2: 'mb-1.5',
  caption: 'mb-1',
  overline: 'mb-1',
};

// Alignment classes
const alignClasses: Record<TypographyAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

// Weight classes
const weightClasses: Record<TypographyWeight, string> = {
  300: 'font-light',
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
};

// Color mapping
const colorMap: Record<TypographyColor, string> = {
  primary: colors.primary,
  secondary: colors.textMuted,
  default: colors.textDefault,
  muted: colors.textWeak,
  error: colors.error,
  success: colors.success,
  warning: colors.warning,
  info: colors.info,
  inherit: 'inherit',
};

// Default HTML elements for each variant
const defaultElements: Record<TypographyVariant, keyof React.JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'p',
  subtitle2: 'p',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
};

// Resolve color - supports predefined names and custom values
const resolveColor = (color: TypographyColor | string): string =>
  color in colorMap ? colorMap[color as TypographyColor] : color;

export const Typography: React.FC<TypographyProps> = ({
  variant = 'body1',
  color = 'default',
  align = 'left',
  weight,
  children,
  className = '',
  as,
  noMargin = false,
}) => {
  const Component = as || defaultElements[variant];
  return React.createElement(
    Component,
    {
      className: cn(
        variantBaseClasses[variant],
        alignClasses[align],
        weight && weightClasses[weight],
        !noMargin && variantMargins[variant],
        className
      ),
      style: { color: resolveColor(color) },
    },
    children
  );
};

// Heading component - convenience wrapper for h1-h6
export const Heading: React.FC<
  Omit<TypographyProps, 'variant'> & { level?: 1 | 2 | 3 | 4 | 5 | 6 }
> = ({ level = 1, ...props }) => (
  <Typography variant={`h${level}` as TypographyVariant} {...props} />
);

// Text component - convenience wrapper for body text
export const Text: React.FC<
  Omit<TypographyProps, 'variant'> & { size?: 'large' | 'default' | 'small' }
> = ({ size = 'default', ...props }) => {
  const variantMap = {
    large: 'body1',
    default: 'body2',
    small: 'caption',
  } as const;
  return <Typography variant={variantMap[size]} {...props} />;
};

// Subtitle component - convenience wrapper for subtitles
export const Subtitle: React.FC<
  Omit<TypographyProps, 'variant'> & { size?: 'large' | 'small' }
> = ({ size = 'large', ...props }) => (
  <Typography variant={size === 'large' ? 'subtitle1' : 'subtitle2'} {...props} />
);

// Caption component - convenience wrapper for captions
export const Caption: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="caption" {...props} />
);

// Overline component - convenience wrapper for overline text
export const Overline: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="overline" {...props} />
);

export default Typography;
